import { Container } from '@mui/material';
import type { Contract } from '@weft/types';
import { CONTRACT_STATUSES } from '@weft/types';
import { isNotEmptyArray, isNullOrUndefined } from '@weft/utils';
import React from 'react';
import { Helmet } from 'react-helmet';
import { withGlobalSearchParams } from '../../components/utility/GlobalSearchParamsBoundary';
import { withSuspenseBoundary } from '../../components/utility/SuspenseBoundary';
import { useContractsByCustomerId } from '../../hooks/requests/use-contracts-by-customer-id';
import { useValidatedParams } from '../../hooks/use-validated-params';
import { ContractPageContent } from './contract-page-components/ContractPageContent';
import { ContractPageFallbackComponent } from './contract-page-components/ContractPageFallbackComponent';
import { ContractPageHeader } from './contract-page-components/ContractPageHeader';

const CustomerContractPage: React.FC = () => {
  const { customerId } = useValidatedParams<['customerId']>('customerId');
  const {
    data: { contracts },
  } = useContractsByCustomerId(customerId);
  const activeContracts = contracts.filter(
    (contract) => contract.status === CONTRACT_STATUSES.Active,
  );
  const customerContract = getLatestContract(
    isNotEmptyArray(activeContracts) ? activeContracts : contracts,
  );

  if (isNullOrUndefined(customerContract)) {
    return <ContractPageFallbackComponent text="No Contracts Found" />;
  }

  return (
    <>
      <Helmet>
        <title>Customer Contract</title>
      </Helmet>
      <Container
        maxWidth="lg"
        sx={{ alignItems: 'center' }}
      >
        <div className="flex w-full justify-center">
          <ContractPageHeader
            customerName={customerContract.customer.name}
            contractId={customerContract.id}
            plan={customerContract.plan}
            contractStartDate={customerContract.startDate}
            contractEndDate={customerContract.endDate}
            status={customerContract.status}
            syncStatus={customerContract.syncStatus}
          />
        </div>
        <ContractPageContent customerContractId={customerContract.id} />
      </Container>
    </>
  );
};

const ContractPageWithSuspenseBoundary = withGlobalSearchParams(withSuspenseBoundary(CustomerContractPage, { errorBoundaryProps: { fallback: () => <ContractPageFallbackComponent text="Commercial Terms Will Show Up Here"/> } }));
export { ContractPageWithSuspenseBoundary as CustomerContractPage };

function getLatestContract(contracts: Contract[]): Contract | undefined {
  const [latestContract] = contracts.sort(
    (a, b) => b.startDate.getTime() - a.startDate.getTime(),
  );

  return latestContract;
}